// D34 release B: an owner combines a game still stored as separate videos into one new video. The parts
// and their AI reports are kept; while the combined video exists and has not failed, it replaces them.
import { gameName, isCombinedVideo, isGameVideo } from './gameParts'

export const MAX_COMBINE = 20

export const combineConfirmText = count => `The ${count} parts become one continuous video. Highlights are copied onto it, and the original parts and their AI reports are kept. Processing takes about as long as the original upload, costs about $2.50–3.20 of video processing per 30 minutes, and videos over 90 minutes can't be AI-analysed yet.`

export const COMBINE_STARTED = 'Combining into one video. It appears here while it processes.'

export const MERGED_PART_NOTICE = 'This part is now part of the full game video.'

// One of the signed-in user's own ready game videos that is neither a combined video nor part of one.
// A ready video whose source is a playlist is one the API refuses: a game uploaded as several files (combined
// at upload, so already one video) or an older upload that kept only its processed playlist. The API checks the
// rest (stored originals, groups, running analysis) and says what is wrong.
export const isCombinable = (upload, username) => Boolean(username) && upload?.owner === username &&
  isGameVideo(upload) && upload.readyForPlayback && !isCombinedVideo(upload) && !upload.mergedInto &&
  !String(upload.source || '').endsWith('.m3u8')

// The order the videos were recorded: date (unknown last), then part number, then name, so "Part 2" is before "Part 10".
const recordedMs = upload => Date.parse(upload.date) || Infinity
const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })
export const combineOrder = uploads => [...(uploads || [])].sort((a, b) =>
  (recordedMs(a) - recordedMs(b)) || ((a.sequenceIndex ?? 0) - (b.sequenceIndex ?? 0)) || byName(a, b))

// The name the API would give the combined video: the first video's game, else its name without " - Part N".
export const defaultCombineName = ordered => ordered?.length ? gameName(ordered[0]) : ''

// A combined video's parts, in playback order.
export const combinedPartIds = upload => [...(upload?.combinedFrom || [])]
  .sort((a, b) => a.sequence - b.sequence)
  .map(part => part.uploadId)

// h:mm:ss
export const formatDuration = seconds => {
  const total = Math.round(Number(seconds) || 0)
  const pad = value => String(value).padStart(2, '0')
  return `${Math.floor(total / 3600)}:${pad(Math.floor(total % 3600 / 60))}:${pad(total % 60)}`
}

// Where an old link to a part goes once its combined video is ready: the combined video, at the copy of the
// highlight when there is one. ?original=1, ?coaching= and ?analysis= stay on the part, so its AI reports,
// coaching runs and references stay reachable.
const STAY_ON_PART = ['original', 'coaching', 'analysis']
export const mergedPartRedirect = (upload, search = '', highlightId = null) => {
  const combined = upload?.mergedInto
  if (!combined?.readyForPlayback) return null
  const params = new URLSearchParams(search)
  if (STAY_ON_PART.some(key => params.has(key))) return null
  const copyId = highlightId ? upload.mergedHighlightMap?.[highlightId] : null
  const query = params.toString()
  return `/upload/${combined.id}${copyId ? `/highlight/${copyId}` : ''}${query ? `?${query}` : ''}`
}

// The query that keeps in-page navigation on an original part that was reached through one of the keys above,
// so a reload, a copied link or Back doesn't redirect to the combined video. Only ?original=1 is kept: the
// others open a dialog or a report once.
export const stayOnPartQuery = (upload, search = '') => {
  const params = new URLSearchParams(search)
  return upload?.mergedInto && STAY_ON_PART.some(key => params.has(key)) ? '?original=1' : ''
}

// Why highlights on this part can't change, or null. A part is writable again once its combined video failed.
export const mergedPartLock = upload => {
  const combined = upload?.mergedInto
  if (!combined || combined.status === 'failed') return null
  return combined.readyForPlayback
    ? 'This part is now part of the full game video. Open the full game to make changes.'
    : 'This part is being combined into one video. Make changes on the full game once it is ready.'
}
